import { FormField, FormSection, StepDetail, StepGlobal } from "../dtos";
import { displayFileName } from "./file-name";
import { fieldRuleErrors } from "./validate";

/**
 * §Decision-gate (ported VERBATIM from workflow-manager-frontend/src/lib/step-blockers.ts, 2026-08-05):
 * mirror the server's two completion gates so the action bar can disable Approve/Submit up front and SAY
 * WHY (hover tooltip), instead of letting the click come back a 400. This is a READ of the same envelope
 * the server gated on — when the two ever disagree the server wins (it re-checks on complete).
 */
export interface StepBlocker {
  kind:
    | "decide-doc"
    | "review-value"
    | "replace-file"
    | "edit-value"
    | "upload-requested-doc"
    | "run-aml-check"
    | "fix-aml-subject"
    // §Two-sided: a card is half-captured, an owner is unconfirmed, or a file predating the
    // front/back flip is still sitting there with no side. Mirrors of assertTwoSidedPairs.
    | "complete-pair"
    | "confirm-pair"
    | "assign-legacy-side"
    // The two gates the server applies to every approval (missingRequiredVisibleFields,
    // assertRequiredDocuments) — mirrored so Submit says what is owed instead of the
    // agent finding out from a 400.
    | "fill-required-field"
    | "upload-required-doc"
    // A value that breaks its field's authored `validate` rule — the third approval gate
    // (`invalidValidatedFields`, 400 "Invalid values for: …").
    | "fix-invalid-value";
  label: string;
}

const hasValue = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== "";

const isPoolStepRef = (ref: unknown): boolean =>
  typeof ref === "string" && /^@s\d+:/.test(ref);

/** §Pool refs: the upstream-mirror key a field reads — a legacy `from` OR an `@s<order>:` ref. */
const mirrorKeyOf = (field: FormField): string | null => {
  const from = (field as { from?: { sourceStepOrder: number; fieldKey: string } }).from;
  if (from) return `@s${from.sourceStepOrder}:${from.fieldKey}`;
  return isPoolStepRef(field.ref) ? (field.ref as string) : null;
};

const fieldKeyOf = (section: FormSection, field: FormField): string => {
  const from = (field as { from?: { sourceStepOrder: number; fieldKey: string } }).from;
  if (from) return `@s${from.sourceStepOrder}:${from.fieldKey}`;
  if (field.ref) return field.ref;
  return field.k ?? `${section.name}.${field.l}`;
};

/** §Dual placement: THIS placement's own visibility — the server stamps `hidden` per placement.
 *  §Live-gates: when the caller supplied its LOCAL verdict, that verdict is the authority — the
 *  server stamp describes the last save, not the branch the agent just picked. */
const placementHidden = (
  placement: StepGlobal,
  aggregateHidden: Set<string>,
  liveAuthority = false
): boolean =>
  liveAuthority
    ? aggregateHidden.has(placement.ref)
    : placement.hidden === true ||
      (placement.hidden === undefined && aggregateHidden.has(placement.ref));

type DocUpload = NonNullable<NonNullable<StepGlobal["document"]>["uploads"]>[number] & {
  supersededAt?: string | null;
};

interface BlockerContext {
  step: StepDetail;
  draft?: Record<string, unknown>;
  aggregateHidden: Set<string>;
  uneditable: Set<string>;
  /** §Live-gates: the hidden set is the caller's LOCAL verdict — it overrides placement stamps */
  liveAuthority: boolean;
}

/** §Two-sided: "Seller National ID (owner 2)" once a document holds several owners, plain otherwise. */
const ownerLabel = (label: string, index: number, total: number): string =>
  total > 1 ? `${label} (owner ${index + 1})` : label;

/**
 * §Two-sided gates — mirrors of the server's assertTwoSidedPairs, in the same order and with the same
 * precedence, so the action bar names exactly what the completion call would reject.
 *
 * A legacy side-less file is reported ALONE and short-circuits the rest: it must be assigned or deleted
 * before the pairs mean anything.
 */
const twoSidedBlockers = (placement: StepGlobal, isHidden: boolean): StepBlocker[] => {
  const document = placement.document;
  if (!document?.twoSided || placement.mode !== "edit" || isHidden) return [];
  const legacy = (document.uploads ?? []).filter((file) => !file.superseded && !file.pairUid);
  if (legacy.length) {
    return legacy.map((file) => ({ kind: "assign-legacy-side" as const, label: displayFileName(file.fileName) }));
  }
  const pairs = document.pairs ?? [];
  const label = placement.label ?? placement.ref;
  if (document.required && !pairs.some((pair) => pair.complete)) {
    return [{ kind: "complete-pair", label }];
  }
  const out: StepBlocker[] = [];
  for (const [index, pair] of pairs.entries()) {
    const owner = ownerLabel(label, index, pairs.length);
    if (!pair.complete) {
      out.push({ kind: "complete-pair", label: owner });
    } else if (!pair.verified) {
      out.push({ kind: "confirm-pair", label: owner });
    }
  }
  return out;
};

/** Gate A — documents: every LIVE file on a flagged, decidable, visible placement must be decided. */
const decideDocBlocker = (placement: StepGlobal, isHidden: boolean): StepBlocker | null => {
  const document = placement.document;
  if (!document?.decisionRequired || !document.decidable || isHidden) return null;
  const undecided = (document.uploads ?? []).some(
    (file) => !file.superseded && file.decision === "PENDING"
  );
  return undecided ? { kind: "decide-doc", label: placement.label ?? placement.ref } : null;
};

/** Gate A — global values: a flagged visible placement holding a value needs its canonical review. */
const reviewValueBlocker = (
  placement: StepGlobal,
  isHidden: boolean,
  ctx: BlockerContext
): StepBlocker | null => {
  if (!placement.reviewRequired || placement.document || isHidden) return null;
  const { draft } = ctx;
  const draftHas =
    placement.mode === "edit" &&
    draft !== undefined &&
    Object.prototype.hasOwnProperty.call(draft, placement.ref);
  const effective = draftHas ? draft?.[placement.ref] : placement.value;
  const draftChanged =
    draftHas && hasValue(draft?.[placement.ref]) && draft?.[placement.ref] !== placement.value;
  const needsReview =
    ((placement.reviewStatus as string | undefined) ?? "PENDING") === "PENDING" || draftChanged;
  return hasValue(effective) && needsReview
    ? { kind: "review-value", label: placement.label ?? placement.ref }
    : null;
};

/** Gate B — an EDIT placement whose canonical value was rejected: editing it clears the block. */
const editValueBlocker = (
  placement: StepGlobal,
  isHidden: boolean,
  ctx: BlockerContext
): StepBlocker | null => {
  if (placement.mode !== "edit" || placement.document || isHidden || ctx.uneditable.has(placement.ref))
    return null;
  return placement.reviewStatus === "REJECTED"
    ? { kind: "edit-value", label: placement.label ?? placement.ref }
    : null;
};

/** Gate A — upstream mirrors: the decision rides in from the SOURCE step via upstreamReviews. */
const upstreamMirrorBlockers = ({ step, aggregateHidden }: BlockerContext): StepBlocker[] => {
  const out: StepBlocker[] = [];
  for (const section of (step.formSchema as FormSection[] | undefined) ?? []) {
    for (const field of section.fields ?? []) {
      const key = mirrorKeyOf(field);
      if (!key || (field as { reviewRequired?: boolean }).reviewRequired !== true) continue;
      if (aggregateHidden.has(key)) continue;
      const review = (step.upstreamReviews as Record<string, { status?: string }> | undefined)?.[key];
      if (
        hasValue((step.upstreamValues as Record<string, unknown> | undefined)?.[key]) &&
        (!review || review.status === "RESUBMITTED")
      ) {
        out.push({ kind: "review-value", label: field.l || key });
      }
    }
  }
  return out;
};

/** Own live REJECTED uploads bound to a visible EDIT document placement (they host Replace). */
const blockedReplaceUploads = (step: StepDetail, aggregateHidden: Set<string>, liveAuthority: boolean): StepBlocker[] => {
  const replaceableUids = new Set<string>();
  for (const placement of (step.globals as StepGlobal[] | undefined) ?? []) {
    if (
      placement.mode !== "edit" ||
      !placement.document ||
      placementHidden(placement, aggregateHidden, liveAuthority)
    )
      continue;
    for (const file of placement.document.uploads ?? []) replaceableUids.add(file.uid);
  }
  return ((step.uploads as DocUpload[] | undefined) ?? [])
    .filter(
      (upload) =>
        !upload.supersededAt && upload.decision === "REJECTED" && replaceableUids.has(upload.uid)
    )
    .map((upload) => ({ kind: "replace-file" as const, label: displayFileName(upload.fileName) }));
};

/** §Doc-request: MANDATORY requested documents on a visible placement must land before completion. */
const requestedDocumentBlockers = ({ step, aggregateHidden, liveAuthority }: BlockerContext): StepBlocker[] => {
  const out: StepBlocker[] = [];
  for (const placement of (step.globals as StepGlobal[] | undefined) ?? []) {
    if (placement.mode !== "edit" || placementHidden(placement, aggregateHidden, liveAuthority)) continue;
    const document = placement.document as
      | {
          request?: { items?: { type: string; status?: string; required?: boolean }[] };
          inboundRequests?: { items?: { type: string; status?: string; required?: boolean }[] }[];
        }
      | null
      | undefined;
    // §Cross-step-request: ARMED asks raised on a review step gate here exactly like the step's
    // own — the server only ships armed rows, so every inbound item it lists is a live obligation.
    const asks = [document?.request, ...(document?.inboundRequests ?? [])];
    for (const ask of asks) {
      for (const item of ask?.items ?? []) {
        if (item.status !== "uploaded" && item.required !== false) {
          out.push({ kind: "upload-requested-doc", label: item.type });
        }
      }
    }
  }
  return out;
};

/** §Manual-AML: a visible edit placement that REQUIRES screening blocks until every file is checked. */
const amlBlockers = ({ step, aggregateHidden, liveAuthority }: BlockerContext): StepBlocker[] => {
  const out: StepBlocker[] = [];
  for (const placement of (step.globals as StepGlobal[] | undefined) ?? []) {
    const document = placement.document;
    if (
      placement.mode !== "edit" ||
      !document?.amlRequired ||
      placementHidden(placement, aggregateHidden, liveAuthority)
    )
      continue;
    // §Two-sided: the unit is the OWNER, not the file. Judging per file would demand a check for the
    // BACK of every card — whose snapshot is the pair's, so it names the same person, but the
    // screening is recorded against the front — and the gate could never clear.
    if (document.twoSided) {
      for (const [index, pair] of (document.pairs ?? []).entries()) {
        const owner = ownerLabel(placement.label ?? placement.ref, index, document.pairs?.length ?? 0);
        const screening = pair.front?.aml ?? pair.back?.aml;
        if (!pair.complete) continue; // twoSidedBlockers already reports this, more precisely.
        if (!pair.actionable) {
          out.push({ kind: "fix-aml-subject", label: owner });
        } else if (!screening || screening.stale) {
          out.push({ kind: "run-aml-check", label: owner });
        }
      }
      continue;
    }
    for (const file of document.uploads ?? []) {
      if (file.superseded) continue;
      if (!file.amlActionable) {
        out.push({ kind: "fix-aml-subject", label: displayFileName(file.fileName) });
      } else if (!file.aml || file.aml.stale) {
        out.push({ kind: "run-aml-check", label: displayFileName(file.fileName) });
      }
    }
  }
  return out;
};

/** Gate B — own rejected field reviews; an edit clears them (server parity incl. readonly guard). */
const ownFieldReviewBlockers = ({ step, uneditable }: BlockerContext): StepBlocker[] => {
  const fieldByKey = new Map<string, FormField>(
    ((step.formSchema as FormSection[] | undefined) ?? []).flatMap((section) =>
      (section.fields ?? []).map((field) => [fieldKeyOf(section, field), field] as const)
    )
  );
  const out: StepBlocker[] = [];
  const reviews = step.fieldReviews ?? {};
  for (const [key, review] of Object.entries(reviews)) {
    if (review.status !== "REJECTED" || uneditable.has(key)) continue;
    const field = fieldByKey.get(key);
    if (!field) continue;
    if ((field as { fromParticipant?: string }).fromParticipant && field.readonly === true) continue;
    out.push({ kind: "edit-value", label: key.split(".").pop() || key });
  }
  return out;
};

/**
 * Mirror of `assertRequiredDocuments` at card level: a required document on a visible,
 * editable placement needs a live file. Superseded files are history and never satisfy it,
 * matching the server, which excludes them from every gate. The read model has already
 * resolved WHICH files belong on this card (own-step plus, for a single-file document, the
 * case-wide copy the server allows), so presence is read off the placement, never guessed.
 *
 * §Two-sided: a front+back document's presence is the PAIR gate's business — it names the
 * owner and the missing side where this could only say "upload it", and the server reports
 * one failure at a time for the same reason.
 */
const requiredDocBlocker = (placement: StepGlobal, isHidden: boolean): StepBlocker | null => {
  const document = placement.document as
    | { required?: boolean; twoSided?: boolean; uploads?: { superseded?: boolean }[] }
    | undefined;
  if (isHidden || !document || document.required !== true || placement.mode === "view") {
    return null;
  }
  if (document.twoSided === true) return null;
  const hasLiveFile = (document.uploads ?? []).some((file) => file.superseded !== true);
  return hasLiveFile
    ? null
    : { kind: "upload-required-doc", label: placement.label ?? placement.ref };
};

/**
 * Mirror of `FormSchemaHelper.missingRequiredFields` as the completion guard calls it: a `req`
 * field blocks when it is not a `ref` placement (a placement carries no submission value — the
 * document gate owns it), not a `from` upstream mirror, not `computed` (auto-filled) or `file`
 * (satisfied by an upload), not hidden, not judged by a live required group, and its value is
 * undefined / null / "" / false — an unticked required consent blocks exactly like an empty text
 * box does.
 *
 * SAVING is deliberately untouched: a draft may be partial, the autosave persists it
 * continuously, and the server accepts it. Only the approval path is gated, there and here.
 */
const requiredFieldBlockers = (ctx: BlockerContext): StepBlocker[] => {
  // The live draft when the caller has one (the step form's values, which the autosave has or is
  // about to persist), else the latest saved submission — the same "case facts merged with this
  // step's own submission" the server judges against.
  const data =
    ctx.draft ??
    ((ctx.step.submissions?.[0]?.data as Record<string, unknown> | undefined) ?? {});
  const out: StepBlocker[] = [];
  for (const section of ((ctx.step.formSchema as FormSection[] | undefined) ?? [])) {
    // §RequiredAnyOf: a grouped member is judged by its group, never on its own — declaring a
    // group relaxes a demand and must never add one.
    const grouped = new Set(
      (
        (section as { requiredGroups?: { members?: string[] }[] }).requiredGroups ?? []
      ).flatMap((group) => group.members ?? [])
    );
    for (const field of section.fields ?? []) {
      if (!field.req || field.ref || field.from) continue;
      if (field.t === "computed" || field.t === "file") continue;
      const key = fieldKeyOf(section, field);
      if (ctx.aggregateHidden.has(key) || grouped.has(key)) continue;
      const value = data[key];
      const empty =
        value === undefined ||
        value === null ||
        value === false ||
        (typeof value === "string" && value.trim() === "");
      if (empty) out.push({ kind: "fill-required-field", label: field.l || key });
    }
  }
  return out;
};

/**
 * Mirror of `invalidValidatedFields`: a value breaking its field's authored `validate` rule
 * refuses the completion, so it must refuse the button too. The message itself is already
 * rendered under the field; here the FIELD is named, because the action bar's job is to say
 * where to go.
 *
 * Judged by the same `fieldRuleErrors` the form displays, over the same facts — so what the
 * agent reads and what the button does can never disagree — and a hidden field is dropped,
 * since a value with no way in cannot be corrected. An unarmed `when` branch skips its rule
 * here exactly as it does on the server.
 */
const invalidValueBlockers = (ctx: BlockerContext): StepBlocker[] => {
  const schema = (ctx.step.formSchema as FormSection[] | undefined) ?? [];
  if (!schema.length) return [];
  const data =
    ctx.draft ??
    ((ctx.step.submissions?.[0]?.data as Record<string, unknown> | undefined) ?? {});
  // §Qualified-self: a rule's `when` may name this step's own answer through the upstream
  // spelling, so the draft shadows both, exactly as the step form builds its gate facts.
  const stepOrder = (ctx.step as { stepOrder?: number }).stepOrder;
  const facts: Record<string, unknown> = {
    ...((ctx.step.preconditionFacts as Record<string, unknown> | undefined) ?? {}),
  };
  for (const [key, value] of Object.entries(data)) {
    if (value === undefined) continue;
    facts[key] = value;
    if (stepOrder !== undefined) facts[`@s${stepOrder}:${key}`] = value;
  }
  const labelByKey = new Map(
    schema.flatMap((section) =>
      (section.fields ?? []).map((field) => [fieldKeyOf(section, field), field.l ?? ""] as const)
    )
  );
  return Object.keys(fieldRuleErrors(schema, facts))
    .filter((key) => !ctx.aggregateHidden.has(key))
    .map((key) => ({
      kind: "fix-invalid-value" as const,
      label: labelByKey.get(key) || key.split(".").pop() || key,
    }));
};

export function computeStepBlockers(
  step: StepDetail,
  draft?: Record<string, unknown>,
  // §Live-gates (product ask, 2026-08-06): the caller's LOCAL visibility verdict — a branch flip
  // reveals/hides fields immediately, and the Submit gate must re-judge on the same beat instead
  // of waiting for the server's stamped sets to refetch.
  liveGates?: { hidden: Set<string>; locked: Set<string> }
): StepBlocker[] {
  const hidden = liveGates?.hidden ?? new Set(step.hiddenFieldKeys ?? []);
  const locked = liveGates?.locked ?? new Set(step.lockedFieldKeys ?? []);
  const ctx: BlockerContext = {
    step,
    draft,
    aggregateHidden: hidden,
    uneditable: new Set([...hidden, ...locked]),
    liveAuthority: liveGates !== undefined,
  };
  const out: StepBlocker[] = [];
  for (const placement of (step.globals as StepGlobal[] | undefined) ?? []) {
    const isHidden = placementHidden(placement, ctx.aggregateHidden, ctx.liveAuthority);
    for (const blocker of [
      requiredDocBlocker(placement, isHidden),
      decideDocBlocker(placement, isHidden),
      reviewValueBlocker(placement, isHidden, ctx),
      editValueBlocker(placement, isHidden, ctx),
    ]) {
      if (blocker) out.push(blocker);
    }
    out.push(...twoSidedBlockers(placement, isHidden));
  }
  out.push(...upstreamMirrorBlockers(ctx));
  out.push(...blockedReplaceUploads(step, ctx.aggregateHidden, ctx.liveAuthority));
  out.push(...requestedDocumentBlockers(ctx));
  out.push(...amlBlockers(ctx));
  out.push(...ownFieldReviewBlockers(ctx));
  out.push(...requiredFieldBlockers(ctx));
  out.push(...invalidValueBlockers(ctx));
  return out;
}

const BLOCKER_VERB: Record<StepBlocker["kind"], string> = {
  "decide-doc": "Decide",
  "review-value": "Review",
  "replace-file": "Replace",
  "edit-value": "Edit",
  "upload-requested-doc": "Upload",
  "run-aml-check": "Run the AML check for",
  "fix-aml-subject": "Correct the extracted name/ID (then run the AML check) for",
  "complete-pair": "Upload both sides for",
  "confirm-pair": "Confirm the extracted values for",
  "assign-legacy-side": "Mark as front or back (or delete)",
  "fix-invalid-value": "Correct",
  "fill-required-field": "Fill",
  "upload-required-doc": "Upload",
};

/** One line naming what is still owed, grouped by the action the user has to take. */
export function blockersTooltip(blockers: StepBlocker[]): string {
  const byKind = new Map<StepBlocker["kind"], string[]>();
  for (const blocker of blockers) {
    const labels = byKind.get(blocker.kind) ?? [];
    if (!labels.includes(blocker.label)) labels.push(blocker.label);
    byKind.set(blocker.kind, labels);
  }
  return [...byKind.entries()]
    .map(([kind, labels]) => `${BLOCKER_VERB[kind]}: ${labels.join(", ")}`)
    .join(" · ");
}
